import { apiRequest } from '../api';
import { state } from '../state';
import { buildPlayerCardElement } from '../card';
import type { PlayerResponse, PlayerWithStats, Race, RatingInput, MyRating, StatKey } from '../types';
import terranIconUrl from '../../assets/race/terran.svg';
import zergIconUrl from '../../assets/race/zerg.svg';
import protossIconUrl from '../../assets/race/protoss.svg';
import randomIconUrl from '../../assets/race/random.svg';
import { STAT_ORDER } from '../radar';
import { buildArchetypeBlock } from '../archetype';
import {
    renderGivenRatingsList,
    renderRatingEditor,
    renderRatingsList,
} from './ratings';
import { applyTranslations, getLocale, onLocaleChange, t } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import { buildFollowButton } from './follow';
import { sharePlayerCard, shareGameCard } from '../share';
import { buildReactionsBlock } from './reactions';
import { loadPlayerReactions, renderPlayerReactions, renderCardReactions, resetPlayerReactions } from './player-reactions';
import { showOverlayGame } from './overlay-output';
import { formatLobbySettings } from './replay-import';

type CardViewMode = 'average' | 'personal' | 'ghost';

const CARD_MODE_STORAGE = 'playerCardMode';

function readCardViewMode(): CardViewMode {
    const raw = localStorage.getItem(CARD_MODE_STORAGE);
    return raw === 'personal' || raw === 'ghost' ? raw : 'average';
}

onLocaleChange(() => {
    applyTranslations();
    if (lastPane && lastPlayer) renderCard(lastPane, lastPlayer);
    // if (lastDescPlayer) renderPlayerDescription(lastDescPlayer);
    buildCardModeToggle();
    renderPlayerLinks();
    renderPlayerReactions();
});

// ============================================================
// Раздел «Ссылки» под карточкой игрока.
// Стримеры привязываются к игрокам в админке (streamers.player_id),
// поэтому просто фильтруем публичный список /api/streamers.
// ============================================================
interface PlayerLink {
    name: string;
    url: string;
    is_live: boolean;
}

let playerLinks: PlayerLink[] = [];
let linksPane: HTMLElement | null = null;

async function loadPlayerLinks(playerId: number): Promise<void> {
    playerLinks = [];
    renderPlayerLinks();

    try {
        const res = await apiRequest<{
            streamers: { name: string; url: string; player_id: number | null; is_live: boolean }[];
        }>('/api/streamers');

        playerLinks = (res.streamers ?? [])
            .filter((s) => s.player_id === playerId)
            .map((s) => ({ name: s.name, url: s.url, is_live: s.is_live === true }));
    } catch (err) {
        console.warn('[player-links] load error:', err);
    }

    renderPlayerLinks();
}

function renderPlayerLinks(): void {
    if (!linksPane) return;
    linksPane.innerHTML = '';

    if (playerLinks.length === 0) {
        linksPane.classList.add('hidden');
        return;
    }
    linksPane.classList.remove('hidden');

    const section = document.createElement('section');
    section.className = 'player-links fade-in';

    const title = document.createElement('h3');
    title.className = 'player-links-title';
    title.textContent = t('player.links_title');
    section.appendChild(title);

    const list = document.createElement('ul');
    list.className = 'player-links-list';

    for (const link of playerLinks) {
        const li = document.createElement('li');

        const a = document.createElement('a');
        a.className = 'player-link';
        a.href = link.url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';

        const icon = document.createElement('span');
        icon.className = 'player-link-icon';
        icon.textContent = '🎬';

        const name = document.createElement('span');
        name.className = 'player-link-name';
        name.textContent = link.name;

        const kind = document.createElement('span');
        kind.className = 'player-link-kind';
        kind.textContent = 'Twitch';

        a.append(icon, name, kind);

        if (link.is_live) {
            const live = document.createElement('span');
            live.className = 'player-link-live';
            live.textContent = t('player.link_live');
            a.appendChild(live);
        }

        li.appendChild(a);
        list.appendChild(li);
    }

    section.appendChild(list);
    linksPane.appendChild(section);
}

export async function openPlayerScreen(playerId: number): Promise<void> {
  showScreen('screen-player');
  // Открытие карточки идёт через pushState, минуя роутер, поэтому сбрасываем
  // скролл вручную — иначе экран открывается на прежней позиции списка.
  window.scrollTo(0, 0);
  await loadPlayerDetail(playerId);
  window.scrollTo(0, 0);
}

function mergePlayerWithDraft(
    base: PlayerWithStats,
    draft: RatingInput
): PlayerWithStats {
    return {
        ...base,
        races: [draft.race],
        tier: draft.tier,
        adaptiveness: draft.adaptiveness,
        greed: draft.greed,
        survival: draft.survival,
        turtle: draft.turtle,
        aggression: draft.aggression,
        variety: draft.variety,
    };
}

/**
 * Возвращает данные для начального состояния редактора:
 * - если у пользователя есть своя оценка — берём её,
 * - иначе — дефолт (раса T, все параметры 3).
 */
async function fetchInitialRating(
    playerId: number,
    dominantRace: Race | null
): Promise<RatingInput> {
    try {
        const res = await apiRequest<{ rating: any | null }>(
            `/api/players/${playerId}/my-rating`,
            { token: state.token }
        );
        const mine = res.rating;
        if (mine) {
            return {
                race: mine.race,
                tier: mine.tier ?? null,
                adaptiveness: mine.adaptiveness,
                greed: mine.greed,
                survival: mine.survival,
                turtle: mine.turtle,
                aggression: mine.aggression,
                variety: mine.variety,
            };
        }
    } catch {
        /* 401 — используем дефолт */
    }

    // Нет своей оценки — выбираем доминирующую расу игрока
    return {
        race: dominantRace ?? 'T',
        tier: null,
        adaptiveness: 3,
        greed: 3,
        survival: 3,
        turtle: 3,
        aggression: 3,
        variety: 3,
    };
}

/**
 * Возвращает сохранённую оценку пользователя (или null, если её нет).
 */
async function fetchMyRating(playerId: number): Promise<MyRating | null> {
    if (!state.token) return null;
    try {
        const res = await apiRequest<{ rating: MyRating | null }>(
            `/api/players/${playerId}/my-rating`,
            {
                token: state.token,
            }
        );
        return res.rating;
    } catch {
        return null;
    }
}

// Напоминание про тиры: показываем, если у своей оценки тир не указан.
const TIER_NOTICE_KEY = 'rating.tierNotice.dismissed';

function tierNoticeDismissed(): boolean {
    return localStorage.getItem(TIER_NOTICE_KEY) === '1';
}

function buildTierNotice(): HTMLElement {
    // Обёртка на всю строку (кнопки переносятся ниже), внутри — плашка
    // шириной как карточка игрока (max-width 960px, по центру).
    const row = document.createElement('div');
    row.className = 'tier-notice-row';

    const box = document.createElement('div');
    box.className = 'tier-notice fade-in';

    const text = document.createElement('span');
    text.className = 'tier-notice-text';
    text.dataset.i18n = 'player.tier_notice';
    text.textContent = t('player.tier_notice');

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'tier-notice-close';
    close.textContent = '×';
    close.title = t('common.close');
    close.setAttribute('aria-label', t('common.close'));
    close.addEventListener('click', (e) => {
        e.stopPropagation();
        localStorage.setItem(TIER_NOTICE_KEY, '1');
        row.remove();
    });

    box.append(text, close);
    row.appendChild(box);
    return row;
}

export async function loadPlayerDetail(playerId: number): Promise<void> {

    const pane = document.getElementById('player-card-pane');
    const editPane = document.getElementById('player-edit-pane');
    const actions = document.getElementById('player-actions');
    const archetypeContainer = document.getElementById('player-archetype-container');
    const ratingsContainer = document.getElementById('ratings-list-container');
    const givenRatingsContainer = document.getElementById('given-ratings-container');
    const hostContainer = document.getElementById('player-host-container');
    linksPane = document.getElementById('player-links-container');
    // const descContainer = document.getElementById('player-description-container');

    if (!pane || !editPane || !actions || !ratingsContainer || !givenRatingsContainer) return;

    // ============================================================
    // Скелетоны — сразу, пока грузим данные
    // ============================================================
    pane.innerHTML = '<div class="skeleton-card"></div>';
    editPane.classList.remove('is-open');
    actions.innerHTML = `
    <div class="skeleton skeleton-line"></div>
    <div class="skeleton skeleton-line" style="width: 180px;"></div>
  `;
    ratingsContainer.innerHTML = `
    <div class="skeleton skeleton-block"></div>
  `;
    givenRatingsContainer.innerHTML = '';
    if (hostContainer) hostContainer.innerHTML = '';
    if (archetypeContainer) archetypeContainer.innerHTML = '';
    // Чужие реакции не должны мигать при переходе на другую карточку.
    resetPlayerReactions();
    document.getElementById('player-reactions-container')?.replaceChildren();
    // if (descContainer) descContainer.innerHTML = '';

    try {
        const { player } = await apiRequest<PlayerResponse>(
            `/api/players/${playerId}`
        );

        // Данные для переключателя оценок над карточкой
        lastPlayerId = playerId;
        lastBasePlayer = player;
        buildCardModeToggle();

        // Описание под карточкой (по оценкам и статистике игр)
        // renderPlayerDescription(player);

        // Карточка в выбранном режиме (средняя / моя / по GHOSTу)
        await renderCardForMode(pane, playerId, cardViewMode);

        // «Ссылки» (Twitch, привязанный к игроку)
        await loadPlayerLinks(playerId);

        // Реакции на игрока — крупный блок под карточкой.
        await loadPlayerReactions(playerId);

        // Залогинен ли пользователь
        const isAuthed = Boolean(state.token && state.user);
        const myRating = isAuthed ? await fetchMyRating(playerId) : null;
        const mine = Boolean(myRating);

        // ============================================================
        // Вспомогательные рендеры кнопок
        // ============================================================

        /** Подсказка «Войдите, чтобы оценивать игроков» */
        const renderLoginHint = (): void => {
            actions.innerHTML = '';

            const hint = document.createElement('p');
            hint.className = 'hint fade-in';
            hint.dataset.i18n = "player.login_to_rate";
            hint.innerHTML = `${t('player.login_to_rate')} `;

            const loginLink = document.createElement('a');
            loginLink.href = '#';
            loginLink.dataset.i18n = "auth.login_link";
            loginLink.textContent = t("auth.login_link")
            loginLink.className = 'hint-link';
            loginLink.addEventListener('click', (e) => {
                e.preventDefault();
                // Запоминаем текущую страницу, чтобы вернуться после входа
                sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
                navigateTo('/auth');
            });

            hint.appendChild(loginLink);
            actions.appendChild(hint);
        };

        /** Кнопки «Изменить»/«Добавить» + «Удалить» */
        const renderViewActions = (): void => {
            editPane.classList.remove('is-open');
            actions.innerHTML = '';

            // Своя оценка есть, но тир не указан — напоминаем обновить её.
            if (mine && myRating && myRating.tier == null && !tierNoticeDismissed()) {
                actions.appendChild(buildTierNotice());
            }

            const editBtn = document.createElement('button');
            editBtn.type = 'button';
            editBtn.className = 'btn-primary fade-in';

            editBtn.dataset.i18n = mine ? 'player.rating_edit' : 'player.rating_add';
            editBtn.textContent = mine ? `${t('player.rating_edit')}` : `${t('player.rating_add')}`;

            editBtn.addEventListener('click', () => void enterEditMode());
            actions.appendChild(editBtn);

            if (mine) {
                const delBtn = document.createElement('button');
                delBtn.type = 'button';
                delBtn.className = 'btn-danger pa-btn--danger fade-in';

                delBtn.dataset.i18n = "player.rating_delete"
                delBtn.textContent = `${t("player.rating_delete")}`;

                delBtn.addEventListener('click', () => {
                    void (async () => {
                        if (!confirm(t('player.rating_delete_confirm'))) return;
                        try {
                            delBtn.disabled = true;

                            delBtn.dataset.i18n = "common.deleting";
                            delBtn.textContent = t("common.deleting");

                            await apiRequest(`/api/players/${playerId}/my-rating`, {
                                method: 'DELETE',
                                token: state.token,
                            });
                            await loadPlayerDetail(playerId);
                        } catch (err) {
                            delBtn.disabled = false;

                            delBtn.dataset.i18n = "player.rating_delete";
                            delBtn.textContent = `🗑 ${t("player.rating_delete")}`;

                            alert(
                                `${t("player.rating_delete_error")}: ` +
                                (err instanceof Error ? err.message : String(err))
                            );
                        }
                    })();
                });
                actions.appendChild(delBtn);
            }

            // Кнопка переименования — только для модераторов/админов
            const canRename = Boolean(state.user?.is_moderator || state.user?.is_admin);
            if (canRename) {
                const renameBtn = document.createElement('button');
                renameBtn.type = 'button';
                renameBtn.className = 'btn-secondary pa-btn--mod fade-in';
                renameBtn.dataset.i18n = "player.name_edit";
                renameBtn.textContent = `${t('player.name_edit')}`;
                renameBtn.title = 'Переименовать игрока';
                renameBtn.addEventListener('click', () => {
                    void (async () => {
                        const next = prompt(
                            `Новое имя для "${player.name}":`,
                            player.name
                        );
                        if (next === null) return;

                        const trimmed = next.trim();
                        if (!trimmed) {
                            alert('Имя не может быть пустым');
                            return;
                        }
                        if (trimmed === player.name) return;

                        try {
                            await apiRequest(`/api/players/${player.id}/name`, {
                                method: 'PATCH',
                                token: state.token,
                                body: { name: trimmed },
                            });
                            await loadPlayerDetail(playerId);
                        } catch (err) {
                            alert('Не удалось переименовать: ' +
                                (err instanceof Error ? err.message : String(err)));
                        }
                    })();
                });
                actions.appendChild(renameBtn);
            }

            // Кнопка редактирования aka — только для модераторов/админов
            const canEditAka = Boolean(state.user?.is_moderator || state.user?.is_admin);
            if (canEditAka) {
                const akaBtn = document.createElement('button');
                akaBtn.type = 'button';
                akaBtn.className = 'btn-secondary pa-btn--mod fade-in';
                akaBtn.textContent = '✎ Aka';
                akaBtn.title = 'Изменить альтернативные имена';
                akaBtn.addEventListener('click', () => {
                    void (async () => {
                        const current = player.aka ?? '';
                        const next = prompt(
                            `Альтернативные имена для "${player.name}" (через запятую):`,
                            current
                        );
                        if (next === null) return; // отмена

                        const trimmed = next.trim();

                        try {
                            await apiRequest(`/api/players/${player.id}/aka`, {
                                method: 'PATCH',
                                token: state.token,
                                body: { aka: trimmed || null },
                            });
                            // Перезагружаем карточку
                            await loadPlayerDetail(playerId);
                        } catch (err) {
                            alert('Не удалось сохранить: ' +
                                (err instanceof Error ? err.message : String(err)));
                        }
                    })();
                });
                actions.appendChild(akaBtn);
            }

            // Кнопка «Следующий» — только в режиме прохода
            if (state.reviewMode?.active) {
                const nextBtn = document.createElement('button');
                nextBtn.type = 'button';
                nextBtn.className = 'btn-primary pa-btn--next fade-in';
                nextBtn.dataset.i18n = "player.next_player";
                nextBtn.textContent = `${t('player.next_player')} →`;

                const { queue, index } = state.reviewMode;
                const isLast = index >= queue.length - 1;

                if (isLast) {
                    // На последнем игроке — кнопка «Завершить»
                    nextBtn.dataset.i18n = "common.finish";
                    nextBtn.textContent = `✓ ${t('common.finish')}`;

                    nextBtn.addEventListener('click', () => {
                        state.reviewMode = null;
                        window.history.pushState({}, '', '/');
                        if (state.onBackToList) {
                            state.onBackToList();
                        }
                    });
                } else {
                    nextBtn.addEventListener('click', () => {
                        void goToNextPlayer();
                    });
                }

                actions.appendChild(nextBtn);
            }
        };

        /** Кнопки «Сохранить»/«Отмена» для режима редактирования */
        const renderEditActions = (onSave: () => void, onCancel: () => void): void => {
            actions.innerHTML = '';

            const saveBtn = document.createElement('button');
            saveBtn.type = 'button';
            saveBtn.className = 'btn-primary fade-in';

            saveBtn.dataset.i18n = "player.edit_save";
            saveBtn.textContent = t('player.edit_save');

            saveBtn.addEventListener('click', onSave);
            actions.appendChild(saveBtn);

            const cancelBtn = document.createElement('button');
            cancelBtn.type = 'button';
            cancelBtn.dataset.i18n = "player.edit_cancel";
            cancelBtn.className = 'btn-secondary fade-in';
            cancelBtn.textContent = t('player.edit_cancel');
            cancelBtn.addEventListener('click', onCancel);
            actions.appendChild(cancelBtn);
        };

        /** Верхнеуровневый выбор: показать подсказку или кнопки */
        const renderActions = (): void => {
            if (!isAuthed) {
                renderLoginHint();
            } else {
                renderViewActions();
            }

            // Статистика игрока доступна всем — и гостям, и авторизованным.
            const statsBtn = document.createElement('button');
            statsBtn.type = 'button';
            statsBtn.className = 'btn-secondary pa-btn--stats fade-in';
            statsBtn.dataset.i18n = 'player.open_stats';
            statsBtn.textContent = t('player.open_stats');
            statsBtn.addEventListener('click', () => {
                navigateTo(`/my-stats?player_id=${player.id}`);
            });
            actions.appendChild(statsBtn);

            // Шорткат «показать в оверлее OBS»: тот же вывод, что в «режиме стримера»,
            // но на 7 секунд и в режиме карточки, выбранном сейчас.
            if (isAuthed) {
                const overlayBtn = document.createElement('button');
                overlayBtn.type = 'button';
                overlayBtn.className = 'btn-secondary pa-btn--overlay fade-in';
                overlayBtn.dataset.i18n = 'player.overlay_show';
                overlayBtn.textContent = t('player.overlay_show');
                overlayBtn.title = t('player.overlay_show_hint');
                overlayBtn.addEventListener('click', () => {
                    void (async () => {
                        overlayBtn.disabled = true;
                        try {
                            const res = await apiRequest<{ state?: { subscribers?: number } }>(
                                '/api/control/show',
                                {
                                    method: 'POST',
                                    token: state.token,
                                    body: { playerId: player.id, viewMode: cardViewMode, autoHideSec: 7 },
                                }
                            );
                            // Нет подключённого источника OBS — показывать некуда
                            const noSource = res?.state?.subscribers === 0;
                            overlayBtn.textContent = noSource
                                ? `⚠ ${t('tools.overlay_no_source_short')}`
                                : t('player.overlay_show_done');
                            if (noSource) overlayBtn.title = t('tools.overlay_no_source');
                            setTimeout(() => {
                                overlayBtn.textContent = t('player.overlay_show');
                                overlayBtn.title = t('player.overlay_show_hint');
                                overlayBtn.disabled = false;
                            }, noSource ? 2600 : 1600);
                        } catch (err) {
                            overlayBtn.disabled = false;
                            alert(`${t('player.overlay_show_error')} ${err instanceof Error ? err.message : String(err)}`);
                        }
                    })();
                });
                actions.appendChild(overlayBtn);
            }

            // «Поделиться» — скачивание картинки карточки.
            const shareBtn = document.createElement('button');
            shareBtn.type = 'button';
            shareBtn.className = 'btn-secondary pa-btn--share fade-in';
            shareBtn.dataset.i18n = 'share.button';
            shareBtn.textContent = `⤓ ${t('share.button')}`;
            shareBtn.title = t('share.download_hint');
            shareBtn.addEventListener('click', () => {
                void sharePlayerCard(player);
            });
            actions.appendChild(shareBtn);

            // Подписка на игрока: уведомления о победах и стримах.
            actions.appendChild(buildFollowButton(player.id));
        };

        // ============================================================
        // Вход в режим редактирования
        // ============================================================
        const enterEditMode = async (): Promise<void> => {
            const formContainer = document.getElementById('rating-form-container');

            // Скелетон в редакторе
            if (formContainer) {
                formContainer.innerHTML = `
          <div class="skeleton skeleton-block" style="height: 480px;"></div>
        `;
            }

            // Открываем панель сразу
            editPane.classList.add('is-open');

            // Пока идёт редактирование — прячем переключатель режимов
            document.getElementById('player-mode-toggle')?.classList.add('hidden');

            // Загружаем начальные данные
            const initial = await fetchInitialRating(playerId, player.dominant_race ?? null);

            let currentDraft: RatingInput = initial;

            const handleSave = (): void => {
                void (async () => {
                    const saveBtn = actions.querySelector('.btn-primary') as HTMLButtonElement | null;
                    if (saveBtn) {
                        saveBtn.disabled = true;

                        saveBtn.dataset.i18n = "player.rating_saving";
                        saveBtn.textContent = t('player.rating_saving');
                    }
                    try {
                        await apiRequest(`/api/players/${playerId}/ratings`, {
                            method: 'POST',
                            token: state.token,
                            body: currentDraft,
                        });
                        await loadPlayerDetail(playerId);
                    } catch (err) {
                        if (saveBtn) {
                            saveBtn.disabled = false;

                            saveBtn.dataset.i18n = "player.edit_save";
                            saveBtn.textContent = t('player.edit_save');
                        }
                        alert(
                            'Не удалось сохранить: ' +
                            (err instanceof Error ? err.message : String(err))
                        );
                    }
                })();
            };

            const handleCancel = (): void => {
                renderCard(pane, player);
                void loadPlayerDetail(playerId);
            };

            // Ставим кнопки «Сохранить»/«Отмена»
            renderEditActions(handleSave, handleCancel);

            // Рендерим сам редактор
            renderRatingEditor(initial, (draft) => {
                currentDraft = draft;
                const preview = mergePlayerWithDraft(player, draft);
                renderCard(pane, preview);
            });

            // Плавное появление формы
            if (formContainer) {
                formContainer.firstElementChild?.classList.add('fade-in');
            }
        };

        // ============================================================
        // Первичный рендер
        // ============================================================
        renderActions();
        await renderPlayerHost(playerId);   // панель ведущего (если игрок — стример)
        await renderPlayerGames(playerId);   // ← НОВОЕ
        const reload = (): void => void loadPlayerDetail(playerId);
        await renderRatingsList(playerId, reload);
        await renderGivenRatingsList(playerId, Boolean(player.user_id), reload);
    } catch (err) {
        pane.innerHTML = `<p class="error fade-in">${t('common.error')}: ${err instanceof Error ? err.message : String(err)
            }</p>`;
        actions.innerHTML = '';
        ratingsContainer.innerHTML = '';
        givenRatingsContainer.innerHTML = '';
    }
}

let lastPane: HTMLElement | null = null;
let lastPlayer: PlayerWithStats | null = null;
let lastPlayerId: number | null = null;
let lastBasePlayer: PlayerWithStats | null = null;
let cardViewMode: CardViewMode = readCardViewMode();

const CARD_MODES: { mode: CardViewMode; key: TranslationKey }[] = [
    { mode: 'average', key: 'player.card_mode_average' },
    { mode: 'personal', key: 'player.card_mode_mine' },
    { mode: 'ghost', key: 'player.card_mode_ghost' },
];

function buildCardModeToggle(): void {
    const container = document.getElementById('player-mode-toggle');
    if (!container) return;
    if (lastPlayerId === null) {
        container.innerHTML = '';
        return;
    }

    container.classList.remove('hidden');
    container.innerHTML = '';

    const isAuthed = Boolean(state.token && state.user);

    for (const item of CARD_MODES) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `player-mode-btn${cardViewMode === item.mode ? ' is-active' : ''}`;
        btn.textContent = t(item.key);

        if (item.mode === 'personal' && !isAuthed) {
            btn.disabled = true;
            btn.title = t('player.login_to_rate');
        }

        btn.addEventListener('click', () => {
            if (cardViewMode === item.mode) return;
            cardViewMode = item.mode;
            localStorage.setItem(CARD_MODE_STORAGE, item.mode);
            buildCardModeToggle();
            if (lastPane && lastPlayerId !== null) {
                void renderCardForMode(lastPane, lastPlayerId, item.mode);
            }
        });

        container.appendChild(btn);
    }
}

/**
 * Shadow-ban: для пользователя, исключённого из расчёта средних, если на
 * игроке нет ни одной учитываемой оценки, показываем ему его собственную
 * оценку — чтобы карточка не была «пустой» (это выдало бы shadow-ban).
 */
async function applyShadowFallback(
    base: PlayerWithStats,
    playerId: number
): Promise<PlayerWithStats> {
    // Есть учитываемые оценки — показываем реальную среднюю.
    if (base.vote_count !== 0) return base;
    // Нужен токен, чтобы получить свою оценку.
    if (!state.token) return base;

    // Если на игроке нет учитываемых оценок (vote_count === 0) и у текущего
    // пользователя есть своя оценка — он гарантированно исключён из средних
    // (иначе его оценка была бы посчитана). Показываем ему его собственную
    // оценку, чтобы карточка не была «пустой» (это выдало бы shadow-ban).
    try {
        const res = await apiRequest<{ rating: MyRating | null }>(
            `/api/players/${playerId}/my-rating`,
            { token: state.token }
        );
        if (!res.rating) return base;

        return {
            ...base,
            races: [res.rating.race],
            vote_count: 1,
            tier: res.rating.tier ?? null,
            adaptiveness: res.rating.adaptiveness,
            greed: res.rating.greed,
            survival: res.rating.survival,
            turtle: res.rating.turtle,
            aggression: res.rating.aggression,
            variety: res.rating.variety,
        };
    } catch {
        return base;
    }
}

async function renderCardForMode(
    pane: HTMLElement,
    playerId: number,
    mode: CardViewMode
): Promise<void> {
    // «Моя» недоступна без авторизации — показываем среднюю.
    if (mode === 'personal' && !(state.token && state.user)) {
        mode = 'average';
    }

    if (mode === 'average') {
        let base = lastBasePlayer;
        if (!base) {
            try {
                const { player } = await apiRequest<PlayerResponse>(`/api/players/${playerId}`);
                base = player;
                lastBasePlayer = player;
            } catch {
                return;
            }
        }
        const shown = await applyShadowFallback(base, playerId);
        renderCard(pane, shown);
        return;
    }

    try {
        const { player } = await apiRequest<PlayerResponse>(
            `/api/players/${playerId}/stats?mode=${mode}`,
            { token: state.token }
        );

        renderCard(pane, player);
    } catch {
        // При ошибке откатываемся к средней.
        if (lastBasePlayer) renderCard(pane, lastBasePlayer);
    }
}

/** Состояние раскрытия типажа — переживает смену режима карточки. */
let archetypeOpen = true;

/** Блок типажа под кнопками оценок: раскрыт по умолчанию, скрывается по клику. */
function buildPlayerArchetype(player: PlayerWithStats): HTMLElement | null {
    const archetype = player.archetype;
    if (!archetype) return null;

    const card = document.createElement('div');
    card.className = `card mystats-card mystats-card--archetype player-archetype-card${archetypeOpen ? ' is-open' : ''}`;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'player-archetype-toggle';
    toggle.setAttribute('aria-expanded', String(archetypeOpen));

    const label = document.createElement('span');
    label.className = 'player-archetype-toggle-label';
    label.textContent = t('mystats.arch_title');

    const chevron = document.createElement('span');
    chevron.className = 'player-archetype-toggle-chevron';
    chevron.textContent = '▾';
    chevron.setAttribute('aria-hidden', 'true');

    toggle.append(label, chevron);
    toggle.addEventListener('click', () => {
        archetypeOpen = !archetypeOpen;
        card.classList.toggle('is-open', archetypeOpen);
        toggle.setAttribute('aria-expanded', String(archetypeOpen));
    });

    const body = document.createElement('div');
    body.className = 'player-archetype-body';
    const inner = document.createElement('div');
    inner.className = 'player-archetype-body-inner';
    // Отдельная обёртка под паддинги: padding у grid-item мешает схлопыванию в 0fr
    const content = document.createElement('div');
    content.className = 'player-archetype-content';

    const hint = document.createElement('p');
    hint.className = 'hint';
    hint.textContent = t('mystats.arch_hint');

    // Выборка ограничена — предупреждаем о приблизительности типажа и тильта
    const note = document.createElement('p');
    note.className = 'hint player-archetype-note';
    note.textContent = t('mystats.arch_disclaimer');

    content.append(hint, note, buildArchetypeBlock(archetype));
    inner.appendChild(content);
    body.appendChild(inner);

    card.append(toggle, body);
    return card;
}

function renderCard(pane: HTMLElement, player: PlayerWithStats): void {
    lastPane = pane;
    lastPlayer = player;

    const newCard = buildPlayerCardElement(player, { compact: false, withReactions: true });
    const existing = pane.querySelector('.player-card');
    if (existing) existing.replaceWith(newCard);
    else {
        pane.innerHTML = '';
        pane.appendChild(newCard);
    }

    // Краткие реакции на карточке — из уже загруженной сводки.
    renderCardReactions();

    // Типаж — отдельная панель под кнопками оценок, обновляем при смене режима/языка.
    const archetypePane = document.getElementById('player-archetype-container');
    if (archetypePane) {
        archetypePane.innerHTML = '';
        const archetypeEl = buildPlayerArchetype(player);
        if (archetypeEl) archetypePane.appendChild(archetypeEl);
    }

    // Клик по метрике внизу карточки → рейтинг, скролл к игроку и его график
    newCard.querySelectorAll<HTMLElement>('.pc-metric').forEach((m) => {
        m.classList.add('is-clickable');
        m.title = t('leaderboard.open_my_chart');
        m.addEventListener('click', () => {
            navigateTo(`/leaderboard?player=${encodeURIComponent(player.name)}`);
        });
    });
}

// ============================================================
// Блок «Описание игрока» — генерируется из оценок и статистики игр
// ============================================================

/**
 * Детерминированное «зерно» из id и ника. Одинаковому игроку всегда
 * достаётся один и тот же набор фраз, а разным — как правило, разный.
 */
function descSeed(player: PlayerWithStats): number {
    let h = (2166136261 ^ player.id) >>> 0;
    for (let i = 0; i < player.name.length; i++) {
        h ^= player.name.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

/**
 * Перемешивающий хэш (финализатор murmur-типа) — разводит выбор по слотам
 * даже при близких значениях зерна, чтобы фразы у разных игроков не совпадали.
 */
function hashMix(x: number): number {
    x = Math.imul(x ^ (x >>> 16), 2246822507);
    x = Math.imul(x ^ (x >>> 13), 3266489909);
    return (x ^ (x >>> 16)) >>> 0;
}

/**
 * Выбирает один из вариантов фразы. Ключ i18n хранит варианты, разделённые
 * `||`. Разные `salt` для разных слотов дают независимый выбор.
 */
function pickPhrase(
    key: TranslationKey,
    seed: number,
    salt: number,
    params?: Record<string, string | number>
): string {
    const variants = t(key, params)
        .split('||')
        .map((s) => s.trim())
        .filter(Boolean);
    if (variants.length === 0) return '';
    const idx = hashMix((seed + Math.imul(salt, 0x9e3779b9)) >>> 0) % variants.length;
    return variants[idx] ?? variants[0] ?? '';
}

/** i18n-ключ интро по расе (с учётом нескольких рас) */
function raceDescKey(player: PlayerWithStats): TranslationKey {
    if (player.races.length === 0) return 'player.desc.race.unknown';
    if (player.races.length > 1) return 'player.desc.race.multi';
    const map: Record<Race, TranslationKey> = {
        T: 'player.desc.race.terran',
        Z: 'player.desc.race.zerg',
        P: 'player.desc.race.protoss',
        R: 'player.desc.race.random',
    };
    return map[player.races[0] as Race];
}

/** Флейвор-ключи для «сильной» стороны каждого параметра */
const DESC_STRONG: Record<StatKey, TranslationKey> = {
    adaptiveness: 'player.desc.strong.adaptiveness',
    aggression: 'player.desc.strong.aggression',
    turtle: 'player.desc.strong.turtle',
    variety: 'player.desc.strong.variety',
    survival: 'player.desc.strong.survival',
    greed: 'player.desc.strong.greed',
};

/** Флейвор-ключи для «слабой» стороны каждого параметра (по-доброму) */
const DESC_WEAK: Record<StatKey, TranslationKey> = {
    adaptiveness: 'player.desc.weak.adaptiveness',
    aggression: 'player.desc.weak.aggression',
    turtle: 'player.desc.weak.turtle',
    variety: 'player.desc.weak.variety',
    survival: 'player.desc.weak.survival',
    greed: 'player.desc.weak.greed',
};

/** Короткие «существительные» для дополнительных упоминаний параметров */
const DESC_TRAIT: Record<StatKey, TranslationKey> = {
    adaptiveness: 'player.desc.trait.adaptiveness',
    aggression: 'player.desc.trait.aggression',
    turtle: 'player.desc.trait.turtle',
    variety: 'player.desc.trait.variety',
    survival: 'player.desc.trait.survival',
    greed: 'player.desc.trait.greed',
};

/** Параметры, отсортированные по убыванию среднего значения */
function rankedStats(player: PlayerWithStats): { key: StatKey; value: number }[] {
    const list: { key: StatKey; value: number }[] = [];
    for (const axis of STAT_ORDER) {
        const value = player[axis.key];
        if (typeof value === 'number') list.push({ key: axis.key, value });
    }
    return list.sort((a, b) => b.value - a.value);
}

/** Уровень активности по числу сыгранных игр */
function activityKey(player: PlayerWithStats): TranslationKey {
    if (player.games_played >= 30) return 'player.desc.activity.high';
    if (player.games_played >= 8) return 'player.desc.activity.mid';
    return 'player.desc.activity.low';
}

/** Уровень результативности по винрейту и среднему месту */
function skillKey(player: PlayerWithStats): TranslationKey {
    const wr = player.winrate;
    const place = player.avg_place;
    if (wr >= 30 || (place != null && place <= 2.5)) return 'player.desc.skill.high';
    if (wr <= 10 || (place != null && place >= 4.5)) return 'player.desc.skill.low';
    return 'player.desc.skill.mid';
}

/**
 * Собирает художественное описание из вариантных фраз (без цифр).
 * Учитывает расу, сильный и второй сильный параметры, слабый и второй
 * слабый параметры, активность и результативность.
 */
function buildDescriptionSentences(player: PlayerWithStats): string[] {
    const seed = descSeed(player);
    const sentences: string[] = [];
    const hasRatings = player.vote_count > 0;
    const hasGames = player.games_played > 0;

    if (hasRatings) {
        sentences.push(pickPhrase(raceDescKey(player), seed, 1));

        const ranked = rankedStats(player);
        if (ranked.length >= 2) {
            const top = ranked[0]!;
            const bottom = ranked[ranked.length - 1]!;

            if (top.value !== bottom.value) {
                // Сильная сторона + второй по силе параметр
                sentences.push(pickPhrase(DESC_STRONG[top.key], seed, 2));
                const second = ranked[1]!;
                if (second.value !== top.value) {
                    sentences.push(pickPhrase('player.desc.also_strong', seed, 6, {
                        trait: t(DESC_TRAIT[second.key]),
                    }));
                }

                // Слабая сторона + второй по слабости параметр
                sentences.push(pickPhrase(DESC_WEAK[bottom.key], seed, 3));
                const secondBottom = ranked[ranked.length - 2]!;
                if (secondBottom.value !== bottom.value) {
                    sentences.push(pickPhrase('player.desc.also_weak', seed, 7, {
                        trait: t(DESC_TRAIT[secondBottom.key]),
                    }));
                }
            }
        }
    } else {
        sentences.push(t('player.desc.no_ratings'));
    }

    // Активность и результативность (по играм)
    if (hasGames) {
        const activity = pickPhrase(activityKey(player), seed, 4);
        const skill = pickPhrase(skillKey(player), seed, 5);
        sentences.push(`${activity} ${skill}`);
    } else {
        sentences.push(t('player.desc.no_games'));
    }

    return sentences;
}

/** Есть ли хоть какие-то данные (оценки или игры) для описания */
function hasDescriptionData(player: PlayerWithStats): boolean {
    return player.vote_count > 0 || player.games_played > 0;
}

/** Рендерит блок описания под карточкой игрока */
export function renderPlayerDescription(player: PlayerWithStats): void {
    const container = document.getElementById('player-description-container');
    if (!container) return;
    container.innerHTML = '';

    // Нет ни оценок, ни игр — блок не показываем
    if (!hasDescriptionData(player)) return;

    const sentences = buildDescriptionSentences(player);
    if (sentences.length === 0) return;

    const card = document.createElement('div');
    card.className = 'card player-description-card';

    const title = document.createElement('h3');
    title.className = 'player-description-title';
    title.textContent = t('player.desc_title');
    card.appendChild(title);

    const text = document.createElement('p');
    text.className = 'player-description-text';
    text.textContent = sentences.join(' ');
    card.appendChild(text);

    container.appendChild(card);
}

function showScreen(id: string): void {
  document.querySelectorAll('.screen').forEach((s) => s.classList.add('hidden'));
  document.getElementById(id)?.classList.remove('hidden');
}

async function goToNextPlayer(): Promise<void> {
    if (!state.reviewMode?.active) return;

    const { queue, index } = state.reviewMode;
    const nextIndex = index + 1;

    if (nextIndex >= queue.length) {
        state.reviewMode = null;
        if (state.onBackToList) state.onBackToList();
        return;
    }

    const next = queue[nextIndex];
    if (!next) return;   // на всякий случай

    state.reviewMode.index = nextIndex;

    pushStateInternal(
        `/?player=${encodeURIComponent(next.name)}`
    );
    await openPlayerScreen(next.id);
}

import type { PlayerGameStats, RaceStat, GameListItem, GamesListResponse, GameFull, GamePlayerEntry } from '../types-games';
import { navigateTo, pushStateInternal } from '../router';

// ============================================================
// Секция «Ведущий игр» (стример)
// ============================================================
interface HostTopItem {
    id?: number;
    name: string;
    count: number;
}

interface PlayerHostResponse {
    host: { id: number; name: string; aka: string | null } | null;
    stats: {
        total_games: number;
        total_players: number;
        avg_players: number;
        last_played_at: string | null;
        top_maps: HostTopItem[];
        top_mods: HostTopItem[];
        top_players: HostTopItem[];
    };
}

async function renderPlayerHost(playerId: number): Promise<void> {
    const container = document.getElementById('player-host-container');
    if (!container) return;

    let res: PlayerHostResponse;
    try {
        res = await apiRequest<PlayerHostResponse>(`/api/players/${playerId}/host-stats`);
    } catch {
        container.innerHTML = `<p class="hint">${t('player.host_load_error')}</p>`;
        return;
    }

    // Игрок не привязан к ведущему — панель не показываем
    if (!res.host) {
        container.innerHTML = '';
        return;
    }

    container.innerHTML = '';
    const card = document.createElement('div');
    card.className = 'card player-host-card';

    // Заголовок
    const header = document.createElement('div');
    header.className = 'player-games-header';

    const title = document.createElement('h3');
    title.textContent = `🎙 ${res.host.name} · ${t('player.host_title')}`;

    const allLink = document.createElement('a');
    allLink.href = `/games?host=${res.host.id}`;
    allLink.className = 'player-games-all-link';
    allLink.textContent = t('player.games_all') + ' →';

    header.append(title, allLink);
    card.appendChild(header);

    const s = res.stats;

    if (s.total_games === 0) {
        const empty = document.createElement('p');
        empty.className = 'hint';
        empty.textContent = t('player.host_games_empty');
        card.appendChild(empty);
        container.appendChild(card);
        return;
    }

    // Сводка
    const summary = document.createElement('div');
    summary.className = 'player-games-summary';

    summary.appendChild(buildStatBlock(t('player.host_games_total'), String(s.total_games)));
    summary.appendChild(buildStatBlock(t('player.host_total_players'), String(s.total_players)));
    summary.appendChild(buildStatBlock(t('player.host_avg_players'), String(s.avg_players)));

    if (s.last_played_at) {
        const d = new Date(s.last_played_at);
        const date = d.toLocaleDateString(getLocale() === 'ru' ? 'ru-RU' : 'en-US', {
            day: '2-digit', month: '2-digit', year: 'numeric',
        });
        summary.appendChild(buildStatBlock(t('player.host_last_game'), date));
    }

    card.appendChild(summary);

    // Топ-5: карты, моды, игроки
    const tops = document.createElement('div');
    tops.className = 'player-host-tops';
    tops.appendChild(buildTopList(
        t('player.host_top_maps'),
        s.top_maps,
        (m) => (m.id != null ? `/games?map=${m.id}` : null)
    ));
    tops.appendChild(buildTopList(
        t('player.host_top_mods'),
        s.top_mods,
        (m) => `/games?mod=${encodeURIComponent(m.name)}`
    ));
    tops.appendChild(buildTopList(
        t('player.host_top_players'),
        s.top_players,
        (p) => `/?player=${encodeURIComponent(p.name)}`
    ));
    card.appendChild(tops);

    container.appendChild(card);
}

/** Секция «Топ-5»: столбик из имён с количеством появлений. */
function buildTopList(
    title: string,
    items: HostTopItem[],
    hrefFor: (item: HostTopItem) => string | null
): HTMLElement {
    const section = document.createElement('div');
    section.className = 'player-host-top';

    const heading = document.createElement('h4');
    heading.className = 'player-host-top-title';
    heading.textContent = title;
    section.appendChild(heading);

    const list = document.createElement('div');
    list.className = 'player-host-top-list';

    if (items.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'hint';
        empty.textContent = '—';
        list.appendChild(empty);
    } else {
        for (const item of items) {
            const href = hrefFor(item);

            const name = document.createElement('span');
            name.className = 'player-host-top-name';
            name.textContent = item.name;
            name.title = item.name; // полное имя при обрезании

            const count = document.createElement('span');
            count.className = 'player-host-top-count';
            count.textContent = `${item.count} ×`;

            if (href) {
                const a = document.createElement('a');
                a.className = 'player-host-top-row is-link';
                a.href = href;
                a.append(name, count);
                list.appendChild(a);
            } else {
                const div = document.createElement('div');
                div.className = 'player-host-top-row';
                div.append(name, count);
                list.appendChild(div);
            }
        }
    }

    section.appendChild(list);
    return section;
}

// ============================================================
// Топ-5 карт/модов/тиммейтов по уже загруженному списку игр.
// Тиммейты считаются только в командных играх (та же команда).
// ============================================================
function computePlayerTops(
    games: GameListItem[],
    playerId: number
): { top_maps: HostTopItem[]; top_mods: HostTopItem[]; top_teammates: HostTopItem[] } {
    const mapCounts = new Map<number, HostTopItem>();
    const modCounts = new Map<string, HostTopItem>();
    const mateCounts = new Map<number, HostTopItem>();

    for (const g of games) {
        if (g.map_id != null && g.map_name) {
            const c = mapCounts.get(g.map_id) ?? { id: g.map_id, name: g.map_name, count: 0 };
            c.count += 1;
            mapCounts.set(g.map_id, c);
        }
        if (g.mod_name) {
            const c = modCounts.get(g.mod_name) ?? { name: g.mod_name, count: 0 };
            c.count += 1;
            modCounts.set(g.mod_name, c);
        }
        if (g.is_team) {
            const myTeam = g.participants.find((p) => p.player_id === playerId)?.team ?? null;
            if (myTeam != null) {
                for (const p of g.participants) {
                    if (p.player_id === playerId || p.team !== myTeam) continue;
                    const c = mateCounts.get(p.player_id) ?? { id: p.player_id, name: p.player_name, count: 0 };
                    c.count += 1;
                    mateCounts.set(p.player_id, c);
                }
            }
        }
    }

    const sortDesc = (a: HostTopItem, b: HostTopItem): number => b.count - a.count;
    return {
        top_maps: [...mapCounts.values()].sort(sortDesc).slice(0, 5),
        top_mods: [...modCounts.values()].sort(sortDesc).slice(0, 5),
        top_teammates: [...mateCounts.values()].sort(sortDesc).slice(0, 5),
    };
}

// ============================================================
// Секция «Игры игрока»
// ============================================================
async function renderPlayerGames(playerId: number): Promise<void> {
    const container = document.getElementById('player-games-container');
    if (!container) return;

    container.innerHTML = '<div class="skeleton skeleton-block" style="height: 200px;"></div>';

    try {
        const [statsRes, gamesRes] = await Promise.all([
            apiRequest<{ stats: PlayerGameStats }>(`/api/players/${playerId}/game-stats`),
            apiRequest<GamesListResponse>(`/api/games?player_id=${playerId}&limit=200`),
        ]);

        const stats = statsRes.stats;
        const games = gamesRes.games;

        renderPlayerGamesContent(container, playerId, stats, games);
    } catch (err) {
        container.innerHTML = `<p class="hint">${t('player.games_load_error')}</p>`;
    }
}

function renderPlayerGamesContent(
    container: HTMLElement,
    playerId: number,
    stats: PlayerGameStats,
    games: GameListItem[]
): void {
    container.innerHTML = '';

    const card = document.createElement('div');
    card.className = 'card player-games-card';

    // Заголовок
    const header = document.createElement('div');
    header.className = 'player-games-header';

    const title = document.createElement('h3');
    title.textContent = t('player.games_title');

    const allLink = document.createElement('a');
    allLink.href = `/games?player_id=${playerId}`;
    allLink.className = 'player-games-all-link';
    allLink.textContent = t('player.games_all') + ' →';

    header.append(title, allLink);
    card.appendChild(header);

    // Если игр нет — показать сообщение и выйти
    if (stats.total_games === 0) {
        const empty = document.createElement('p');
        empty.className = 'hint';
        empty.textContent = t('player.games_empty');
        card.appendChild(empty);
        container.appendChild(card);
        return;
    }

    // Сводка
    const summary = document.createElement('div');
    summary.className = 'player-games-summary';

    summary.appendChild(buildStatBlock(
        t('player.games_total'),
        String(stats.total_games)
    ));
    summary.appendChild(buildStatBlock(
        t('player.games_wins'),
        String(stats.total_wins)
    ));
    summary.appendChild(buildStatBlock(
        t('player.games_winrate'),
        `${stats.winrate}%`
    ));
    if (stats.favorite_race) {
        const raceBlock = buildStatBlock(
            t('player.games_favorite_race'),
            stats.favorite_race
        );
        raceBlock.querySelector('.stat-value')?.classList.add(`stat-value--race-${stats.favorite_race}`);
        summary.appendChild(raceBlock);
    }
    if (stats.favorite_format) {
        summary.appendChild(buildStatBlock(
            t('player.games_favorite_format'),
            stats.favorite_format
        ));
    }

    card.appendChild(summary);

    // ============================================================
    // Статистика по расам — только если игрок играл более чем на одной
    // ============================================================
    const raceStats = stats.race_stats ?? [];
    if (raceStats.length > 1) {
        const racesTitle = document.createElement('h4');
        racesTitle.className = 'player-games-races-title';
        racesTitle.textContent = t('player.games_races_title');

        const racesGrid = document.createElement('div');
        racesGrid.className = 'player-games-races';

        for (const rs of raceStats) {
            const block = buildRaceStatBlock(rs);
            racesGrid.appendChild(block);
        }

        const racesSection = document.createElement('div');
        racesSection.className = 'player-games-races-section';
        racesSection.append(racesTitle, racesGrid);

        card.appendChild(racesSection);
    }

    // ============================================================
    // Топ-5: карты, моды, тиммейты — по сыгранным играм игрока
    // ============================================================
    const topsSection = document.createElement('div');
    topsSection.className = 'player-games-tops-section';

    const topsTitle = document.createElement('h4');
    topsTitle.className = 'player-games-races-title';
    topsTitle.textContent = t('player.games_tops_title');

    const tops = computePlayerTops(games, playerId);
    const topsGrid = document.createElement('div');
    topsGrid.className = 'player-host-tops';
    topsGrid.appendChild(buildTopList(
        t('player.games_top_maps'),
        tops.top_maps,
        (m) => (m.id != null ? `/games?map=${m.id}` : null)
    ));
    topsGrid.appendChild(buildTopList(
        t('player.games_top_mods'),
        tops.top_mods,
        (m) => `/games?mod=${encodeURIComponent(m.name)}`
    ));
    topsGrid.appendChild(buildTopList(
        t('player.games_top_teammates'),
        tops.top_teammates,
        (p) => `/?player=${encodeURIComponent(p.name)}`
    ));

    topsSection.append(topsTitle, topsGrid);
    card.appendChild(topsSection);

    // Список последних игр: показываем 3, остальные — раскрываются по кнопке
    const VISIBLE_GAMES = 3;

    const renderRow = (g: GameListItem): HTMLElement =>
        buildGameRow(
            g,
            playerId,
            g.participants.find((p) => p.player_id === playerId)?.player_name ?? ''
        );

    const list = document.createElement('div');
    list.className = 'player-games-list';

    for (const g of games.slice(0, VISIBLE_GAMES)) {
        list.appendChild(renderRow(g));
    }
    card.appendChild(list);

    if (games.length > VISIBLE_GAMES) {
        const details = document.createElement('div');
        details.className = 'player-games-details';

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'player-games-toggle';
        toggle.textContent = t('player.games_show_all');

        const expandable = document.createElement('div');
        expandable.className = 'player-games-expandable';

        const restList = document.createElement('div');
        restList.className = 'player-games-rest';
        for (const g of games.slice(VISIBLE_GAMES)) {
            restList.appendChild(renderRow(g));
        }

        expandable.appendChild(restList);
        details.append(toggle, expandable);
        card.appendChild(details);

        toggle.addEventListener('click', () => {
            const open = details.classList.toggle('is-open');
            toggle.textContent = open
                ? t('player.games_hide_all')
                : t('player.games_show_all');
        });
    }

    container.appendChild(card);
}

function buildStatBlock(label: string, value: string): HTMLElement {
    const block = document.createElement('div');
    block.className = 'stat-block';

    const labelEl = document.createElement('div');
    labelEl.className = 'stat-label';
    labelEl.textContent = label;

    const valueEl = document.createElement('div');
    valueEl.className = 'stat-value';
    valueEl.textContent = value;

    block.append(labelEl, valueEl);
    return block;
}

function buildRaceStatBlock(rs: RaceStat): HTMLElement {
    const block = document.createElement('div');
    block.className = 'race-stat-block';

    const iconMap: Record<Race, string> = {
        T: terranIconUrl,
        Z: zergIconUrl,
        P: protossIconUrl,
        R: randomIconUrl,
    };

    const icon = document.createElement('img');
    icon.className = `race-stat-icon race-stat-icon-${rs.race}`;
    icon.src = iconMap[rs.race];
    icon.alt = rs.race;
    icon.loading = 'lazy';

    const info = document.createElement('div');
    info.className = 'race-stat-info';

    const gamesEl = document.createElement('div');
    gamesEl.className = 'race-stat-games';
    gamesEl.textContent = `${rs.games} ${t('player.games_games_word')}`;

    const winsEl = document.createElement('div');
    winsEl.className = 'race-stat-wins';
    winsEl.textContent = `${rs.wins} ${t('player.games_wins_word')} · ${rs.winrate}%`;

    info.append(gamesEl, winsEl);

    block.append(icon, info);
    return block;
}

function buildGameRow(g: GameListItem, playerId: number, playerName: string): HTMLElement {
    const row = document.createElement('a');
    row.className = 'game-row';
    row.href = `/games?q=${encodeURIComponent(playerName)}&highlight=${g.id}`;
    // ЛКМ открывает всплывающее окно с игрой; Ctrl/Cmd-клик — как раньше,
    // открывает игру в списке (средняя кнопка тоже работает штатно).
    row.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        // Роутер слушает клики по <a> на document и не смотрит defaultPrevented,
        // поэтому гасим всплытие, чтобы не уехать на страницу игр.
        e.stopPropagation();
        void openGameDetailModal(g.id);
    });

    // Находим себя среди участников
    const me = g.participants.find((p) => p.player_id === playerId);

    // Дата
    const date = document.createElement('span');
    date.className = 'game-row-date';
    const d = new Date(g.played_at);
    date.textContent = d.toLocaleDateString(getLocale() === 'ru' ? 'ru-RU' : 'en-US', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
    });

    // Формат
    const format = document.createElement('span');
    format.className = 'game-row-format';
    format.textContent = g.format_name ?? '—';

    // Результат игрока
    const result = document.createElement('span');
    result.className = 'game-row-result';
    if (me) {
        if (me.is_winner) {
            result.textContent = '👑 ' + t('player.games_result_win');
            result.classList.add('is-win');
        } else if (me.eliminated_at !== null) {
            const place = g.participants.length - me.eliminated_at + 1;
            result.textContent = `#${place}`;
            result.classList.add('is-loss');
        } else {
            result.textContent = '—';
        }
    } else {
        result.textContent = '—';
    }

    // Карта
    const map = document.createElement('span');
    map.className = 'game-row-map';
    map.textContent = g.map_name ?? '';

    const delta = me ? buildPlayerDeltaBadge(me) : null;

    row.append(date, format, result, map);
    if (delta) row.insertBefore(delta, map);
    return row;
}

/** Бейдж дельт Elo и activity за игру (из rating_history) для строки игры игрока. */
function buildPlayerDeltaBadge(me: GameListItem['participants'][number]): HTMLElement | null {
    if (me.elo_delta == null && me.activity_delta == null) return null;
    const badge = document.createElement('span');
    badge.className = 'game-row-delta';

    if (me.elo_delta != null) {
        const elo = document.createElement('span');
        elo.className = 'game-row-delta-elo' + (me.elo_delta >= 0 ? ' is-plus' : ' is-minus');
        elo.textContent = `${me.elo_delta >= 0 ? '+' : ''}${me.elo_delta}`;
        badge.appendChild(elo);
    }

    if (me.activity_delta != null) {
        const act = document.createElement('span');
        act.className = 'game-row-delta-activity';
        const v = Number(me.activity_delta);
        act.textContent = `${v >= 0 ? '+' : ''}${v.toFixed(1)}`;
        badge.appendChild(act);
    }

    return badge;
}

// ============================================================
// Всплывающее окно с информацией об игре (открывается из истории игр игрока)
// ============================================================
let gameDetailOverlay: HTMLElement | null = null;
let currentGameDetail: GameFull | null = null;

function ensureGameDetailModal(): HTMLElement | null {
    if (gameDetailOverlay && document.body.contains(gameDetailOverlay)) return gameDetailOverlay;

    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay hidden game-detail-overlay';

    const modal = document.createElement('div');
    modal.className = 'modal game-detail-modal';

    const header = document.createElement('div');
    header.className = 'modal-header';

    const title = document.createElement('h3');
    title.textContent = t('player.game_details_title');

    const actions = document.createElement('div');
    actions.className = 'modal-header-actions';

    // Вывод превью игры в OBS-оверлей (как в разделе «Игры»)
    const overlayBtn = document.createElement('button');
    overlayBtn.type = 'button';
    overlayBtn.className = 'modal-close game-detail-overlay-btn';
    overlayBtn.title = t('games.overlay_show_hint');
    overlayBtn.setAttribute('aria-label', t('games.overlay_show'));
    overlayBtn.textContent = '📺';
    overlayBtn.addEventListener('click', () => {
        const game = currentGameDetail;
        if (!game || !state.token) return;
        void (async () => {
            overlayBtn.disabled = true;
            const ok = await showOverlayGame(game.id);
            overlayBtn.textContent = ok ? '✓' : '⚠';
            setTimeout(() => {
                overlayBtn.textContent = '📺';
                overlayBtn.disabled = false;
            }, 1600);
        })();
    });
    actions.appendChild(overlayBtn);

    const shareBtn = document.createElement('button');
    shareBtn.type = 'button';
    shareBtn.className = 'modal-close';
    shareBtn.title = t('share.download_hint');
    shareBtn.setAttribute('aria-label', t('share.button'));
    shareBtn.textContent = '⤓';
    shareBtn.addEventListener('click', () => {
        if (currentGameDetail) void shareGameCard(currentGameDetail);
    });
    actions.appendChild(shareBtn);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'modal-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '✕';
    closeBtn.addEventListener('click', () => closeGameDetailModal());
    actions.appendChild(closeBtn);

    header.append(title, actions);

    const body = document.createElement('div');
    body.className = 'game-detail-body';

    modal.append(header, body);
    overlay.appendChild(modal);

    // Закрытие: клик по фону и Escape.
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) closeGameDetailModal();
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && gameDetailOverlay && !gameDetailOverlay.classList.contains('hidden')) {
            closeGameDetailModal();
        }
    });

    document.body.appendChild(overlay);
    gameDetailOverlay = overlay;
    return overlay;
}

function closeGameDetailModal(): void {
    gameDetailOverlay?.classList.add('hidden');
}

export async function openGameDetailModal(gameId: number): Promise<void> {
    const overlay = ensureGameDetailModal();
    if (!overlay) return;
    const body = overlay.querySelector<HTMLElement>('.game-detail-body');
    if (body) body.innerHTML = '<div class="skeleton skeleton-block" style="height: 160px;"></div>';
    // Кнопка оверлея — только для залогиненных (нужен JWT)
    const ovwBtn = overlay.querySelector<HTMLButtonElement>('.game-detail-overlay-btn');
    if (ovwBtn) ovwBtn.style.display = state.token && state.user ? '' : 'none';
    overlay.classList.remove('hidden');

    try {
        const res = await apiRequest<{ game: GameFull }>(`/api/games/${gameId}`);
        if (body) renderGameDetailContent(body, res.game);
    } catch {
        if (body) body.innerHTML = `<p class="hint">${t('player.games_load_error')}</p>`;
    }
}

function renderGameDetailContent(body: HTMLElement, g: GameFull): void {
    body.innerHTML = '';
    currentGameDetail = g;
    const total = g.players.length;

    // Мета-информация об игре
    const meta = document.createElement('div');
    meta.className = 'game-detail-meta';

    const d = new Date(g.played_at);
    const dateStr = d.toLocaleString(getLocale() === 'ru' ? 'ru-RU' : 'en-US', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
    meta.appendChild(buildDetailMetaItem(t('games.field_date'), dateStr));
    if (g.format) {
        meta.appendChild(buildDetailMetaItem(
            t('games.field_format'),
            g.format.name + (g.format.is_team ? ` · ${t('games.team_label')}` : '')
        ));
    }
    if (g.duration_min) {
        meta.appendChild(buildDetailMetaItem(
            t('player.game_details_duration'),
            `${g.duration_min} ${t('games.minutes_short')}`
        ));
    }
    if (g.map) meta.appendChild(buildDetailMetaItem(t('games.field_map'), g.map.name));
    if (g.mod) meta.appendChild(buildDetailMetaItem(t('games.field_mod'), g.mod.name));
    if (g.host) meta.appendChild(buildDetailMetaItem(t('games.host_label'), g.host.name));
    if (g.lobby_settings?.length) {
        const lobby = buildDetailMetaItem(t('games.lobby_settings'), formatLobbySettings(g.lobby_settings));
        lobby.classList.add('game-detail-meta-item--wide');
        meta.appendChild(lobby);
    }
    body.appendChild(meta);

    // Победитель(и)
    const winnerRow = document.createElement('div');
    winnerRow.className = 'game-detail-winner';
    const winnerLabel = document.createElement('span');
    winnerLabel.className = 'game-detail-winner-label';
    winnerLabel.textContent = t('games.winner_label');
    winnerRow.appendChild(winnerLabel);

    const winners = g.players.filter((p) => p.is_winner);
    if (winners.length === 0) {
        const none = document.createElement('span');
        none.className = 'game-player-summary-empty';
        none.textContent = t('games.no_winner');
        winnerRow.appendChild(none);
    } else {
        winners.forEach((p, i) => {
            if (i > 0) {
                const sep = document.createElement('span');
                sep.className = 'game-player-sep';
                sep.textContent = '+';
                winnerRow.appendChild(sep);
            }
            const el = document.createElement('span');
            el.className = 'game-detail-winner-name';
            el.style.setProperty('--race-color', getDetailRaceColor(p.race));
            el.append(buildDetailRaceIcon(p.race, 'game-detail-race'), document.createTextNode(p.player_name));
            makeDetailPlayerClickable(el, p.player_id);
            winnerRow.appendChild(el);
        });
    }
    body.appendChild(winnerRow);

    // Участники
    const playersTitle = document.createElement('h4');
    playersTitle.className = 'game-detail-players-title';
    playersTitle.textContent = `${t('games.show_all_players')} (${total})`;
    body.appendChild(playersTitle);

    const list = document.createElement('div');
    list.className = 'game-detail-players';
    if (g.format?.is_team) renderDetailTeamPlayers(list, g.players);
    else renderDetailSoloPlayers(list, g.players);
    body.appendChild(list);

    // Заметки
    if (g.notes) {
        const notesTitle = document.createElement('h4');
        notesTitle.className = 'game-detail-players-title';
        notesTitle.textContent = t('player.game_details_notes');
        const notes = document.createElement('p');
        notes.className = 'game-detail-notes';
        notes.textContent = g.notes;
        body.append(notesTitle, notes);
    }

    // Реакции и голос за MVP.
    body.appendChild(
        buildReactionsBlock(
            g.id,
            g.players.map((p) => ({ player_id: p.player_id, player_name: p.player_name })),
        ),
    );
}

function buildDetailMetaItem(label: string, value: string): HTMLElement {
    const item = document.createElement('div');
    item.className = 'game-detail-meta-item';

    const l = document.createElement('span');
    l.className = 'game-detail-meta-label';
    l.textContent = label;

    const v = document.createElement('span');
    v.className = 'game-detail-meta-value';
    v.textContent = value;

    item.append(l, v);
    return item;
}

const detailRaceIconUrl: Record<Race, string> = {
    T: terranIconUrl,
    Z: zergIconUrl,
    P: protossIconUrl,
    R: randomIconUrl,
};

function buildDetailRaceIcon(race: Race, className = 'player-line-race'): HTMLImageElement {
    const img = document.createElement('img');
    img.className = className;
    img.src = detailRaceIconUrl[race] ?? randomIconUrl;
    img.alt = race;
    img.loading = 'lazy';
    return img;
}

function getDetailRaceColor(race: string): string {
    switch (race) {
        case 'T': return 'var(--race-terran)';
        case 'Z': return 'var(--race-zerg)';
        case 'P': return 'var(--race-protoss)';
        case 'R': return 'var(--race-random)';
        default: return 'var(--text-muted)';
    }
}

function makeDetailPlayerClickable(el: HTMLElement, playerId: number): void {
    el.classList.add('game-clickable');
    el.addEventListener('click', (e) => {
        e.stopPropagation();
        closeGameDetailModal();
        void openPlayerScreen(playerId);
    });
}

/** Место участника: 1 — победитель, иначе scale - eliminated_at + 1. */
function entryPlaceDetail(p: GamePlayerEntry, scale: number): number | null {
    if (p.eliminated_at === null) return p.is_winner ? 1 : null;
    return scale - p.eliminated_at + 1;
}

function buildDetailPlayerLine(p: GamePlayerEntry, total: number): HTMLElement {
    const place = entryPlaceDetail(p, total);
    const row = document.createElement('div');
    row.className = 'player-line';
    if (p.is_winner) row.classList.add('is-winner');
    if (!p.is_winner && place !== null) row.classList.add('is-eliminated');
    row.style.setProperty('--race-color', getDetailRaceColor(p.race));

    const placeEl = document.createElement('span');
    placeEl.className = 'player-line-place';
    if (place === 1) {
        placeEl.textContent = '🥇';
        placeEl.title = t('games.place_1');
    } else if (place !== null) {
        placeEl.textContent = place === 2 ? '🥈' : place === 3 ? '🥉' : `#${place}`;
    } else {
        placeEl.textContent = '—';
        placeEl.title = t('games.not_eliminated');
    }
    row.appendChild(placeEl);

    row.appendChild(buildDetailRaceIcon(p.race));

    const nameEl = document.createElement('span');
    nameEl.className = 'player-line-name';
    nameEl.textContent = p.player_name;
    if (p.player_aka) nameEl.title = `aka ${p.player_aka}`;
    makeDetailPlayerClickable(nameEl, p.player_id);
    row.appendChild(nameEl);

    const delta = buildPlayerDeltaBadge(p);
    if (delta) row.appendChild(delta);

    return row;
}

function renderDetailSoloPlayers(container: HTMLElement, players: GamePlayerEntry[]): void {
    const total = players.length;
    const sorted = [...players].sort((a, b) => {
        const pa = entryPlaceDetail(a, total) ?? Number.MAX_SAFE_INTEGER;
        const pb = entryPlaceDetail(b, total) ?? Number.MAX_SAFE_INTEGER;
        if (pa !== pb) return pa - pb;
        return a.player_name.localeCompare(b.player_name);
    });
    for (const p of sorted) container.appendChild(buildDetailPlayerLine(p, total));
}

function renderDetailTeamPlayers(container: HTMLElement, players: GamePlayerEntry[]): void {
    const total = players.length;
    const teamMap = new Map<number, GamePlayerEntry[]>();
    for (const p of players) {
        const n = p.team ?? 0;
        const arr = teamMap.get(n) ?? [];
        arr.push(p);
        teamMap.set(n, arr);
    }

    const teamBest = (members: GamePlayerEntry[]): number => {
        if (members.some((p) => p.is_winner)) return 1;
        const places = members
            .map((p) => entryPlaceDetail(p, total))
            .filter((x): x is number => x !== null);
        return places.length ? Math.min(...places) : Number.MAX_SAFE_INTEGER;
    };

    const teams = [...teamMap.entries()].sort((a, b) => {
        const pa = teamBest(a[1]);
        const pb = teamBest(b[1]);
        if (pa !== pb) return pa - pb;
        return a[0] - b[0];
    });

    for (const [teamNum, members] of teams) {
        const isWinner = members.some((p) => p.is_winner);
        const hasEliminated = members.some((p) => p.eliminated_at !== null);

        const teamRow = document.createElement('div');
        teamRow.className = 'player-team-line';
        if (isWinner) teamRow.classList.add('is-winner');
        if (!isWinner && hasEliminated) teamRow.classList.add('is-eliminated');

        const labelEl = document.createElement('span');
        labelEl.className = 'player-team-label';
        labelEl.textContent = `${t('games.team_label')} ${teamNum}${isWinner ? ' 👑' : ''}`;
        teamRow.appendChild(labelEl);
        container.appendChild(teamRow);

        const playersEl = document.createElement('div');
        playersEl.className = 'player-team-players';
        members
            .slice()
            .sort((a, b) => {
                const pa = entryPlaceDetail(a, total) ?? Number.MAX_SAFE_INTEGER;
                const pb = entryPlaceDetail(b, total) ?? Number.MAX_SAFE_INTEGER;
                if (pa !== pb) return pa - pb;
                return a.player_name.localeCompare(b.player_name);
            })
            .forEach((p) => {
                const row = buildDetailPlayerLine(p, total);
                row.classList.add('player-team-player');
                playersEl.appendChild(row);
            });

        container.appendChild(playersEl);
    }
}